import React, { useState, useEffect, useCallback } from "react";
import {
  ScanLine, Upload, Loader2, CheckCircle2, XCircle, AlertTriangle, FileText, Receipt,
} from "lucide-react";
import { Card, Button, Badge, Spinner, Modal, EmptyState, Select } from "../lib/ui";
import { useT } from "../lib/i18n";
import { useToast } from "../lib/toast";
import { compressImage } from "../lib/image";
import api from "../lib/api";
import { rp, InvoiceViewerModal } from "../components/purchasing";

const STATUS_TONE = { NEEDS_REVIEW: "warn", APPROVED: "success", REJECTED: "danger" };
const STATUS_LABEL = { NEEDS_REVIEW: "Perlu Review", APPROVED: "Disetujui", REJECTED: "Ditolak" };
const ITEM_TONE = { AUTO: "success", MATCHED: "success", REVIEW: "warn", UNMATCHED: "danger" };

// ---------------- Review modal ----------------
function ReviewModal({ draftId, open, onClose, onChanged, products }) {
  const { t } = useT();
  const toast = useToast();
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!draftId) return;
    api.get(`/purchasing/ocr/${draftId}`).then(({ data }) => setDraft(data)).catch(() => toast.error(t("Failed to load")));
  }, [draftId, t, toast]);
  useEffect(() => { if (open) load(); else setDraft(null); }, [open, load]);

  const setMatch = async (index, product_id) => {
    const { data } = await api.patch(`/purchasing/ocr/${draftId}`, { items: [{ index, matched_product_id: product_id }] });
    setDraft(data);
  };
  const setField = async (index, key, value) => {
    const { data } = await api.patch(`/purchasing/ocr/${draftId}`, { items: [{ index, [key]: Number(value) || 0 }] });
    setDraft(data);
  };

  const decide = async (action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/purchasing/ocr/${draftId}/${action}`, {});
      if (action === "approve") toast.success(`${t("Approved")} → ${data.reference_no}`);
      else toast.success(t("Draft rejected"));
      onChanged?.(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("Something went wrong"));
    } finally { setBusy(false); }
  };

  const unresolved = (draft?.items || []).filter((it) => !it.matched_product_id).length;
  const pending = draft?.status === "NEEDS_REVIEW";

  return (
    <Modal open={open} onClose={onClose} size="xl"
      title={draft ? `${t("Review Invoice")} · ${draft.supplier_name || t("Unknown supplier")}` : t("Review Invoice")}
      footer={draft && pending ? (
        <>
          <Button variant="danger" onClick={() => decide("reject")} loading={busy} data-testid="ocr-reject-btn"><XCircle size={16} /> {t("Reject")}</Button>
          <Button variant="success" onClick={() => decide("approve")} loading={busy} disabled={unresolved > 0} data-testid="ocr-approve-btn"><CheckCircle2 size={16} /> {t("Approve & Create Purchase")}</Button>
        </>
      ) : <Button variant="outline" onClick={onClose}>{t("Close")}</Button>}>
      {!draft ? <div className="flex h-40 items-center justify-center"><Spinner className="h-7 w-7" /></div> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft">
            <Badge tone={STATUS_TONE[draft.status]}>{t(STATUS_LABEL[draft.status])}</Badge>
            <span>· {t("Invoice")}: <b className="text-ink">{draft.invoice_no || "—"}</b></span>
            <span>· {t("Date")}: {draft.invoice_date || "—"}</span>
            <span>· {t("Total")}: <b className="text-ink">{rp(draft.total_value)}</b></span>
          </div>
          {unresolved > 0 && pending && (
            <div className="flex items-center gap-2 rounded-lg bg-warn-light px-3 py-2 text-xs text-warn-text" data-testid="ocr-review-warning">
              <AlertTriangle size={15} /> {unresolved} {t("item(s) still need a product match before approval")}
            </div>
          )}
          <div className="overflow-x-auto rounded-xl border border-line">
            <table className="w-full text-sm" data-testid="ocr-review-table">
              <thead className="bg-stone-100 text-left text-xs uppercase text-ink-mute">
                <tr><th className="px-3 py-2">{t("OCR Text")}</th><th className="px-3 py-2">{t("Matched Product")}</th><th className="px-3 py-2 text-right">{t("Qty")}</th><th className="px-3 py-2 text-right">{t("Unit Price")}</th><th className="px-3 py-2 text-right">{t("Total")}</th></tr>
              </thead>
              <tbody className="divide-y divide-line">
                {draft.items.map((it, i) => (
                  <tr key={i} data-testid={`ocr-item-${i}`}>
                    <td className="px-3 py-2">
                      <p className="font-medium text-ink">{it.ocr_name}</p>
                      <Badge tone={ITEM_TONE[it.status]} className="!px-1.5 !py-0.5 !text-[10px]">{it.status} {it.confidence != null ? `${Math.round(it.confidence * 100)}%` : ""}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {pending ? (
                        <select className="cf-input !py-1.5 !text-sm" value={it.matched_product_id || ""} onChange={(e) => setMatch(i, e.target.value)} data-testid={`ocr-match-${i}`}>
                          <option value="">{t("— choose product —")}</option>
                          {it.candidates?.length > 0 && <optgroup label={t("Suggestions")}>{it.candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</optgroup>}
                          <optgroup label={t("All products")}>{products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}</optgroup>
                        </select>
                      ) : <span className="text-ink">{it.matched_product_name || "—"}</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {pending ? <input type="number" className="cf-input !w-20 !py-1.5 !text-right !text-sm" value={it.quantity} onChange={(e) => setField(i, "quantity", e.target.value)} data-testid={`ocr-qty-${i}`} /> : num(it.quantity)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {pending ? <input type="number" className="cf-input !w-24 !py-1.5 !text-right !text-sm" value={it.unit_price} onChange={(e) => setField(i, "unit_price", e.target.value)} data-testid={`ocr-price-${i}`} /> : rp(it.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold text-ink">{rp(it.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {draft.invoice_image && <img src={draft.invoice_image} alt="invoice" className="mx-auto max-h-64 rounded-lg border border-line" />}
        </div>
      )}
    </Modal>
  );
}

const num = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });

// ---------------- OCR tab ----------------
export default function OcrTab({ products }) {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewId, setReviewId] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/purchasing/ocr"); setData(data); }
    catch { toast.error(t("Failed to load")); }
  }, [t, toast]);
  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const img = await compressImage(file, 1280, 0.8);
      const { data } = await api.post("/purchasing/ocr/upload", { image: img, source: "web_upload" });
      toast.success(t("Invoice scanned — review the extracted items"));
      await load();
      setReviewId(data.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("OCR failed. Try a clearer photo."));
    } finally { setUploading(false); e.target.value = ""; }
  };

  const c = data?.counts || {};
  return (
    <div className="space-y-4">
      <Card className="flex flex-col items-center gap-3 border-dashed py-8 text-center" data-testid="ocr-upload-card">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-light text-primary"><ScanLine size={26} /></div>
        <div>
          <h3 className="font-heading text-lg font-bold text-ink">{t("Scan Invoice Photo")}</h3>
          <p className="text-sm text-ink-soft">{t("Upload a supplier invoice — AI extracts items, matches products, then you review & approve.")}</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary/90">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? t("Scanning invoice…") : t("Upload Invoice Photo")}
          <input type="file" accept="image/*" className="hidden" onChange={onUpload} disabled={uploading} data-testid="ocr-upload-input" />
        </label>
      </Card>

      <Card>
        <div className="mb-3 flex items-center gap-2">
          <h3 className="font-heading font-bold text-ink">{t("Invoice Review Queue")}</h3>
          {c.needs_review > 0 && <Badge tone="warn" data-testid="ocr-pending-count">{c.needs_review} {t("pending")}</Badge>}
        </div>
        {!data ? <div className="flex h-24 items-center justify-center"><Spinner className="h-6 w-6" /></div>
          : data.rows.length === 0 ? <EmptyState title={t("No scanned invoices yet")} subtitle={t("Upload an invoice photo to get started.")} />
          : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ocr-queue-table">
                <thead className="border-b border-line text-left text-xs uppercase tracking-wide text-ink-mute">
                  <tr><th className="py-2 pr-3">{t("Date")}</th><th className="py-2 pr-3">{t("Supplier")}</th><th className="py-2 pr-3">{t("Invoice")}</th><th className="py-2 pr-3 text-center">{t("Items")}</th><th className="py-2 pr-3 text-right">{t("Total")}</th><th className="py-2 pr-3">{t("Status")}</th><th className="py-2 pr-3" /></tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {data.rows.map((r) => (
                    <tr key={r.id} data-testid={`ocr-row-${r.id}`}>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{(r.created_at || "").slice(0, 10)}</td>
                      <td className="py-2.5 pr-3 text-ink">{r.supplier_name || "—"}</td>
                      <td className="py-2.5 pr-3 text-xs text-ink-soft">{r.invoice_no || "—"}</td>
                      <td className="py-2.5 pr-3 text-center">{r.item_count}{r.needs_review_count > 0 && <Badge tone="warn" className="ml-1 !px-1.5 !py-0.5 !text-[10px]">{r.needs_review_count}</Badge>}</td>
                      <td className="py-2.5 pr-3 text-right font-semibold text-ink">{rp(r.total_value)}</td>
                      <td className="py-2.5 pr-3"><Badge tone={STATUS_TONE[r.status]}>{t(STATUS_LABEL[r.status])}</Badge></td>
                      <td className="py-2.5 pr-3 text-right">
                        {r.status === "NEEDS_REVIEW"
                          ? <Button variant="outline" size="sm" onClick={() => setReviewId(r.id)} data-testid={`ocr-review-${r.id}`}><FileText size={14} /> {t("Review")}</Button>
                          : r.purchase_ref ? <span className="font-mono text-xs text-primary">{r.purchase_ref}</span> : <Button variant="ghost" size="sm" onClick={() => setReviewId(r.id)}>{t("View")}</Button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      <ReviewModal draftId={reviewId} open={!!reviewId} onClose={() => setReviewId(null)} onChanged={load} products={products} />
      <InvoiceViewerModal purchaseId={invoiceId} open={!!invoiceId} onClose={() => setInvoiceId(null)} />
    </div>
  );
}
